import './EmailBox.scss';

import type { EmailActivityAnswer } from '@app/@types/redux/answer';
import type { SimulationEmail } from '@app/@types/redux/pageElement';
import { Alert, Col, H1, ImagePlaceholder, Row } from '@components/UI/Html';
import NoStyleDiv from '@components/UI/Html/NoStyleDiv';
import { errorToast, getParsedEmailBody, successToast } from '@libs/helper';
import { useAppSelector } from '@state/redux/store';
import { translate } from '@state/utils/helper';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  FaAsterisk,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaImage,
  FaLink,
  FaLock,
  FaPaperclip,
  FaPen,
  FaSmile,
  FaTextHeight,
} from 'react-icons/fa';
import { Button } from 'reactstrap';

import WYSIWYGEditor from '@components/UI/Html/WYSIWYG';
import { Attachments } from '.';
import smile from '../../assets/images/icons/smile.svg';

interface EmailBoxProps {
  email: SimulationEmail;
  sendFn: () => void;
  setEmailAnswer: (value: EmailActivityAnswer) => void;
}
const EmailBox = ({ email, sendFn, setEmailAnswer }: EmailBoxProps) => {
  const [message, setMessage] = useState<string>('');
  const [sendModal, setSendModal] = useState(false);

  const name = useAppSelector((state) => state.users.user.attributes.name);
  const senderEmail = email.attributes.simulationEmail.attributes.senderEmail;
  const senderImage = email.attributes.simulationEmail.attributes.senderImage;
  const attachments = email.attributes.simulationEmail.attributes.attachments;
  const emailSentAt = email.attributes.sentAt;
  const subject = email.attributes.simulationEmail.attributes.subject;
  const body = getParsedEmailBody(name, email.attributes.simulationEmail.attributes.body);

  function onBlur(value: string) {
    setMessage(value);
  }

  useEffect(() => {
    setEmailAnswer({
      body: message,
      to: senderEmail,
      subject: `RE: ${subject}`,
      simulationEmailId: email.attributes.simulationEmail.id.toString(),
      sentAt: emailSentAt ?? '',
    });
  }, [message]);

  return (
    <div className="email-chatbox-container">
      <div className="email-chatbox-body">
        <div className="email-form">
          <InputWithPills label="To" defaultPills={[senderEmail]} avatar={senderImage} />
          <EmailInput label="Re" value={subject} />

          <div className="email-field">
            <WYSIWYGEditor
              value={message}
              onBlur={(value) => {
                onBlur(value);
              }}
              placeholder="Eg. Type your reply here..."
              height="600px"
            />
            <div className="fixed-text">
              {translate('email.on')} {moment(emailSentAt).format('MMMM Do YYYY, h:mm:ss a')},
              <span style={{ margin: '0 6px', fontWeight: 700 }}>{senderEmail}</span>
              {translate('email.wrote')}:
              <br />
              <div className="pl-4 ml-2" style={{ borderLeft: 'solid 1px #d8d8d8' }}>
                <NoStyleDiv>{body}</NoStyleDiv>
              </div>
              {attachments.length && attachments.length > 0 ? (
                <Attachments attachments={attachments} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="email-toolbar">
        <Button
          color="primary"
          onClick={() => {
            if (!message.trim()) {
              errorToast('Email content is required');
              return;
            }
            setSendModal(true);
          }}
        >
          {translate('email.send')}
        </Button>
        <div className="icon-group">
          <FaAsterisk className="icon" size={12} />
          <FaTextHeight className="icon" />
          <FaPaperclip className="icon" />
          <FaLink className="icon" />
          <FaSmile className="icon" />
          <FaExclamationTriangle className="icon" />
          <FaImage className="icon" />
          <FaLock className="icon" />
          <FaPen className="icon" />
          <FaCalendarAlt className="icon" />
        </div>
      </div>

      <Alert
        isOpen={sendModal}
        onRequestClose={() => {
          setSendModal(false);
        }}
        showCloseButton
      >
        <Row className="py-3">
          <Col>
            <ImagePlaceholder src={smile} height="60" width="60" native />
          </Col>
        </Row>
        <Row className="pt-3">
          <Col>
            <H1 className="font-weight-500 text-dark">{translate('email.sendConfirmation')}</H1>
          </Col>
        </Row>
        <Row className="pt-2 pb-3">
          <Col>
            <p className="px-3 font-weight-400 text-dark">
              {translate('email.sendConfirmationDescription')}
            </p>
          </Col>
        </Row>
        <Row>
          <Col className="px-1 mx-0 mb-2 text-center text-sm-right">
            <Button
              color="grey"
              onClick={() => {
                setSendModal(false);
              }}
            >
              {translate('email.cancel')}
            </Button>
          </Col>
          <Col className="px-1 mx-0 mb-2 text-center text-sm-left">
            <Button
              color="info"
              onClick={() => {
                setSendModal(false);
                successToast(translate('email.success'));
                sendFn();
              }}
            >
              {translate('email.send')}
            </Button>
          </Col>
        </Row>
      </Alert>
    </div>
  );
};
export default EmailBox;
interface InputWithPillsProps {
  label: string;
  defaultPills: string[];
  avatar: string;
}
const InputWithPills = ({ label, defaultPills, avatar }: InputWithPillsProps) => {
  // const [inputValue, setInputValue] = useState('');
  // const [pills, setPills] = useState<string[]>(defaultPills ?? ['rohan.saroah@cartedo.com']);

  // const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (e.key === 'Enter' && inputValue.trim()) {
  //     setPills([...pills, inputValue.trim()]);
  //     setInputValue('');
  //   }
  // };

  return (
    <div className="input-with-pills">
      <div className="input-with-pills-container">
        <label className="fixed-label">{label}: </label>
        <div className="input-wrapper">
          {defaultPills.map((pill, index) => (
            <div className="email-input" key={index}>
              <img className="email-icon" alt="..." src={avatar} />
              <span className="email-text">{pill}</span>
            </div>
          ))}
          {/* <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder=""
          /> */}
        </div>
      </div>
    </div>
  );
};

interface InputProps {
  label: string;
  value?: string;
}

const EmailInput: React.FC<InputProps> = ({ label, value }) => {
  const [inputValue, setInputValue] = useState(value);

  return (
    <div className="input-container">
      <label className="fixed-input-label">{label}: </label>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        className="simple-input"
        readOnly
      />
    </div>
  );
};
